import { AUTH_TOKEN_KEYS, TIMEOUT_CONFIG } from '../constants/common';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { API_ENDPOINTS } from '../constants/api';
import { removeLocalItem } from '../utils/config/localStorage';

class AuthService {
  /**
   * Login user with email and password
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response
   */
  async login(credentials) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, credentials);
      const processedResponse = ResponseHandler.handleSuccess(response, 'Login successful');
      return processedResponse;
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Login failed');
    }
  }

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  async register(userData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.REGISTER, userData);
      const processedResponse = ResponseHandler.handleSuccess(response, 'Registration successful');
      return processedResponse;
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Registration failed');
    }
  }

  /**
   * Register subscriber user
   * @param {Object} userData - Subscriber registration data
   * @returns {Promise<Object>} Registration response
   */
  async registerSubscriber(userData) {
    try {
      // Use extended timeout for signup API (20 seconds)
      const response = await apiClient.post(API_ENDPOINTS.SUBSCRIBER_REGISTER, userData, {
        timeout: TIMEOUT_CONFIG.SIGNUP,
      });
      const processedResponse = ResponseHandler.handleSuccess(response, 'Registration successful');
      return processedResponse;
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Registration failed');
    }
  }

  /**
   * Clear authentication tokens from localStorage
   */
  clearTokens() {
    removeLocalItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN);
    removeLocalItem(AUTH_TOKEN_KEYS.USER_TYPE);
    removeLocalItem(AUTH_TOKEN_KEYS.USER_ID);
    // Also clear Matrix credentials to avoid cross-user leakage in shared browsers.
    removeLocalItem(AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN);
    removeLocalItem(AUTH_TOKEN_KEYS.MATRIX_USER_ID);
    removeLocalItem(AUTH_TOKEN_KEYS.MATRIX_DEVICE_ID);
    removeLocalItem(AUTH_TOKEN_KEYS.MATRIX_ACCESS_TOKEN_AT);
    removeLocalItem(AUTH_TOKEN_KEYS.MATRIX_INBOX_SNAPSHOT);
    removeLocalItem(AUTH_TOKEN_KEYS.LOGIN_TIME_LAST_SYNC_AT);
    removeLocalItem(AUTH_TOKEN_KEYS.IS_LOGIN_WITH_SUPPORT_PASSWORD);
  }

  /**
   * Logout user
   * @returns {Promise<Object>} Logout response
   */
  async logout() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGOUT);
      
      // Clear tokens from localStorage
      this.clearTokens();
      
      return ResponseHandler.handleSuccess(response, 'Logout successful');
    } catch (error) {
      // Even if logout fails on server, clear local tokens
      this.clearTokens();
      throw ResponseHandler.handleError(error, 'Logout failed');
    }
  }


  /**
   * Forgot password
   * @param {string} email - User email
   * @returns {Promise<Object>} Forgot password response
   */
  async forgotPassword(email) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
      return ResponseHandler.handleSuccess(response, 'Password reset email sent');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to send password reset email');
    }
  }

  /**
   * Verify reset password token (valid / invalid / expired)
   * @param {string} token - Reset token from link
   * @returns {Promise<Object>} { success, valid, message?, expired? }
   */
  async verifyResetToken(token) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.VERIFY_RESET_TOKEN, { token });
      const data = response.data?.data ?? response.data;
      const valid = data?.valid === true;
      return {
        success: true,
        valid,
        message: response.data?.message,
        expired: data?.expired,
      };
    } catch (error) {
      const response = error.response;
      const data = response?.data?.data ?? response?.data;
      return {
        success: false,
        valid: false,
        message: response?.data?.message || error.message || 'Invalid or expired link.',
        expired: data?.expired,
      };
    }
  }

  /**
   * Reset password
   * @param {Object} resetData - Reset password data
   * @param {string} resetData.token - Reset token
   * @param {string} resetData.password - New password
   * @returns {Promise<Object>} Reset password response
   */
  async resetPassword(resetData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.RESET_PASSWORD, resetData);
      return ResponseHandler.handleSuccess(response, 'Password reset successful');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Password reset failed');
    }
  }

  /**
   * Verify confirm token (check if email confirmation link is valid before confirming).
   * @param {string} userId - User ID from link
   * @param {string} token - Email verification token from link
   * @returns {Promise<Object>} { success, valid, message, data: { alreadyVerified, expired, rol } }
   */
  async verifyConfirmToken(userId, token) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.VERIFY_CONFIRM_TOKEN, { userId, token });
      const data = response.data?.data ?? response.data;
      return {
        success: true,
        valid: data?.valid === true,
        message: response.data?.message,
        data: {
          alreadyVerified: data?.alreadyVerified === true,
          expired: data?.expired === true,
          rol: data?.rol,
        },
      };
    } catch (error) {
      const res = error.response;
      const data = res?.data?.data ?? res?.data;
      return {
        success: res?.status !== 500,
        valid: false,
        message: res?.data?.message || error.message || 'Invalid link.',
        data: {
          alreadyVerified: data?.alreadyVerified === true,
          expired: data?.expired === true,
          rol: data?.rol,
        },
      };
    }
  }

  /**
   * Verify email
   * @param {string} token - Email verification token
   * @returns {Promise<Object>} Email verification response
   */
  async verifyEmail(token) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.VERIFY_EMAIL, { token });
      return ResponseHandler.handleSuccess(response, response?.data?.message || 'Email verified successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Email verification failed');
    }
  }

  /**
   * Resend verification email
  */
  async resendVerification(payload) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.RESEND_VERIFICATION, payload);
      return ResponseHandler.handleSuccess(response, 'Verification email sent successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to resend verification email');
    }
  }

  /**
   * Check username availability
   * @param {string} username - Username to check
   * @returns {Promise<Object>} Username check response
   */
  async checkUsername(username) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.CHECK_USERNAME}/${username}`);
      return ResponseHandler.handleSuccess(response, 'Username check successful');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Username check failed');
    }
  }

  /**
   * Register creator
   * @param {Object} registrationData - Creator registration data
   * @param {string} subType - Subscription type
   * @returns {Promise<Object>} Creator registration response
   */
  async registerCreator(registrationData, subType) {
    try {
      // Use extended timeout for signup API (20 seconds)
      const response = await apiClient.post(`${API_ENDPOINTS.CREATOR_REGISTER}/${subType}`, registrationData, {
        timeout: TIMEOUT_CONFIG.SIGNUP,
      });
      return ResponseHandler.handleSuccess(response, 'Creator registration successful');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Creator registration failed');
    }
  }

}

export default new AuthService();
